import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { Layout } from "@/components/Layout";
import { AuthModal } from "@/components/AuthModal";

/* ── Lazy-loaded pages ── */
import Home from "@/pages/Home";
const Category = lazy(() => import("@/pages/Category"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Cart = lazy(() => import("@/pages/Cart"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const Wishlist = lazy(() => import("@/pages/Wishlist"));
const Orders = lazy(() => import("@/pages/Orders"));
const OrderDetail = lazy(() => import("@/pages/OrderDetail"));
const Account = lazy(() => import("@/pages/Account"));
const NotFound = lazy(() => import("@/pages/NotFound"));

/* ── Legal & Company Pages ── */
const PrivacyPolicy = lazy(() => import("@/pages/legal/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("@/pages/legal/TermsAndConditions"));
const RefundPolicy = lazy(() => import("@/pages/legal/RefundPolicy"));
const ReturnPolicy = lazy(() => import("@/pages/legal/ReturnPolicy"));
const ShippingPolicy = lazy(() => import("@/pages/legal/ShippingPolicy"));
const CancellationPolicy = lazy(() => import("@/pages/legal/CancellationPolicy"));
const CookiePolicy = lazy(() => import("@/pages/legal/CookiePolicy"));
const Accessibility = lazy(() => import("@/pages/legal/Accessibility"));
const PaymentInformation = lazy(() => import("@/pages/legal/PaymentInformation"));
const AboutUs = lazy(() => import("@/pages/company/AboutUs"));
const ContactUs = lazy(() => import("@/pages/company/ContactUs"));
const Faq = lazy(() => import("@/pages/company/Faq"));
const SizeGuide = lazy(() => import("@/pages/company/SizeGuide"));

/* ── Admin Pages ── */
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("@/pages/admin/AdminProducts"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminOrderDetail = lazy(() => import("@/pages/admin/AdminOrderDetail"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));

/* ── Loading Spinner ── */
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-fashion-bg">
      <div className="w-6 h-6 border-2 border-fashion-text border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

/* ── Scroll to top on route change ── */
function ScrollToTop() {
  const { pathname } = window.location;
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

/* ── Remove boot splash ── */
function BootSplashDismiss() {
  useEffect(() => {
    const splash = document.getElementById("fm-fashion-splash");
    if (splash) {
      splash.style.transition = "opacity 0.4s ease";
      splash.style.opacity = "0";
      setTimeout(() => { try { splash.remove(); } catch {} }, 450);
    }
  }, []);
  return null;
}

function AppRoutes() {
  return (
    <Layout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Home */}
          <Route path="/" element={<Home />} />

          {/* Fashion Categories */}
          <Route path="/new-arrivals" element={<Category />} />
          <Route path="/collections" element={<Category />} />
          <Route path="/search" element={<Category />} />
          <Route path="/:gender" element={<Category />} />
          <Route path="/:gender/:subcategory" element={<Category />} />

          {/* Product */}
          <Route path="/product/:slug" element={<ProductDetail />} />

          {/* Commerce */}
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/wishlist" element={<Wishlist />} />

          {/* Account */}
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders/:id" element={<OrderDetail />} />
          <Route path="/account" element={<Account />} />

          {/* Legal Pages */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/return-policy" element={<ReturnPolicy />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/cancellation-policy" element={<CancellationPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="/payment-information" element={<PaymentInformation />} />

          {/* Company Pages */}
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/size-guide" element={<SizeGuide />} />

          {/* Admin Pages */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="orders/:id" element={<AdminOrderDetail />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="settings" element={<div className="p-8 text-center text-gray-500">Settings Panel (Coming Soon)</div>} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
              <ScrollToTop />
              <BootSplashDismiss />
              <AppRoutes />
              <AuthModal />
              <Toaster
                position="top-center"
                toastOptions={{
                  style: {
                    background: "#FFFFFF",
                    border: "1px solid var(--fashion-border)",
                    color: "var(--fashion-text)",
                    fontFamily: "Inter, system-ui, sans-serif",
                    fontSize: "14px",
                  },
                }}
              />
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
